import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { AuthorizerService } from '@markant-platform/authorizer/data-access';
import { BehaviorSubject, catchError, Observable, of, switchMap, tap } from 'rxjs';
import {
  EMPTY_PAGE,
  MappedTableData,
  PaginationPageSize,
  SortAttributes,
  SortDirections,
  TableDraft,
  TableDraftBuilder,
} from '@markant-platform/shared/table/model';
import { TableService } from '@markant-platform/shared/table/feature';
import {
  AssignedRole,
  CartridgeRoleAssignmentFlow,
  CartridgeWithSelectableRoles,
  Group,
  SelectableRole,
  VariantRoleAssignmentLimitResponse,
} from '@markant-platform/authorizer/model';
import { AppConfigService, AppFeature, CMSLanguageService, Page } from '@markant-platform/shared/data-access';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthenticatedUserService } from '@markant-platform/shared/user/data-access';
import { UserNameDto } from '@markant-platform/shared/user/model';
import {
  CartridgeRoleAssignmentDataService,
  DisabledRoleLimitsStrategyService,
  GroupRoleLimitsStrategyService,
} from '@markant-platform/shared/authorizer-utils';
import { DEFAULT_SORT, HEADER_AND_TABLE_CELL } from './configuration/group-assignment-detail.config';

@Component({
  selector: 'markant-platform-group-assignment-detail',
  templateUrl: './group-assignment-detail.component.html',
  styleUrls: ['./group-assignment-detail.component.scss'],
})
export class GroupAssignmentDetailComponent implements OnInit {
  @Input() groupId = '';
  @Input() organizationId?: string;

  group!: Group;
  cartridgeRoleAssignments!: CartridgeWithSelectableRoles[];
  groupName = '';
  mappedTableData!: MappedTableData;
  initialLoad = true;
  tableIsLoading = true;
  pageIndex = 1;
  pageSize: PaginationPageSize = 25;
  sortAttributes: SortAttributes = DEFAULT_SORT;
  totalCount = 0;
  isGroupMemberDialogOpen = false;
  isUsageRightDialogOpen = false;
  forceUpdate$ = new BehaviorSubject<void>(undefined);
  forceTableDataUpdate$ = new BehaviorSubject<void>(undefined);
  tableDraft: TableDraft = new TableDraftBuilder(HEADER_AND_TABLE_CELL)
    .sortableColumns(['firstName', 'lastName', 'email'])
    .sort(DEFAULT_SORT)
    .disableRowLink(true)
    .tableVariant('accent')
    .build();

  protected readonly CartridgeRoleAssignmentFlow = CartridgeRoleAssignmentFlow;
  readonly isRoleLimitEnabled = AppConfigService.isFeatureEnabled(AppFeature.ROLE_LIMITS);

  private readonly authorizerService = inject(AuthorizerService);
  private readonly authenticatedUserService = inject(AuthenticatedUserService);
  private readonly tableService = inject(TableService);
  private readonly cmsLanguageService = inject(CMSLanguageService);
  private readonly cartridgeRoleAssignmentDataService = inject(CartridgeRoleAssignmentDataService);
  private readonly groupRoleLimitsStrategyService = inject(GroupRoleLimitsStrategyService);
  private readonly disabledRoleLimitsStrategyService = inject(DisabledRoleLimitsStrategyService);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    const limitService = this.isRoleLimitEnabled
      ? this.groupRoleLimitsStrategyService
      : this.disabledRoleLimitsStrategyService;

    this.cartridgeRoleAssignmentDataService.init(limitService);

    this.getOrganizationId();

    this.cmsLanguageService.activeCMSLanguage$
      .pipe(
        switchMap(() => this.forceUpdate$),
        switchMap(() => this.loadGroup()),
        switchMap(() => this.forceTableDataUpdate$),
        switchMap(() => this.loadTableData()),
        switchMap(() => this.getRoleAssignedLimits(this.organizationId ?? '')),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(roleLimits => {
        this.initialLoad = false;
        this.updateRoleData(this.group.roleAssignments?.serviceRoleAssignments ?? [], roleLimits);
      });
  }

  loadGroup(): Observable<Group> {
    return this.authorizerService.getGroupById(this.groupId, { includeRoles: true }).pipe(
      tap(group => {
        this.group = group;
        this.groupName = group.displayName;
      })
    );
  }

  reloadGroup(): void {
    this.forceUpdate$.next();
  }

  onCloseGroupMemberDialog(): void {
    this.isGroupMemberDialogOpen = false;
  }

  loadTableData(): Observable<Page<UserNameDto>> {
    this.tableIsLoading = true;

    return this.authorizerService
      .getPagedGroupMembers(this.groupId, this.pageIndex - 1, this.pageSize, this.sortAttributes)
      .pipe(
        catchError(() => of(EMPTY_PAGE as Page<UserNameDto>)),
        tap(({ totalCount, result }) => {
          this.tableIsLoading = false;
          this.totalCount = totalCount;
          this.mappedTableData = this.tableService.returnMappedTableData(result, this.tableDraft);
        })
      );
  }

  onIndexChange(index: number): void {
    if (index && index !== this.pageIndex) {
      this.pageIndex = index;
      this.forceTableDataUpdate$.next();
    }
  }

  onPageSizeChange(size: PaginationPageSize): void {
    if (size) {
      this.pageIndex = 1;
      this.pageSize = size;

      this.forceTableDataUpdate$.next();
    }
  }

  openRoleAssignmentDialog(): void {
    this.cartridgeRoleAssignmentDataService.resetSelectedRoles();
    this.isUsageRightDialogOpen = true;
  }

  onClickEditMembers(): void {
    this.isGroupMemberDialogOpen = true;
  }

  onSortChange(event: { columnId: string; sortDirection: SortDirections }): void {
    this.sortAttributes = {
      sortField: event.columnId,
      sortOrder: event.sortDirection,
    };

    this.tableDraft = {
      ...this.tableDraft,
      sortStartColumnId: event.columnId,
      sortStartDirection: event.sortDirection,
    };

    this.forceTableDataUpdate$.next();
  }

  updateGroupNameInUI(newName: string): void {
    this.groupName = newName;
  }

  onMemberSuccess(): void {
    this.forceUpdate$.next();
  }

  private getOrganizationId(): void {
    if (!this.organizationId) {
      this.authenticatedUserService
        .getOrganizationId()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(organizationId => (this.organizationId = organizationId));
    }
  }

  private getRoleAssignedLimits(organizationId: string): Observable<VariantRoleAssignmentLimitResponse[]> {
    if (!organizationId) {
      return of([]);
    }

    return this.authorizerService.getRoleAssignmentLimits(organizationId).pipe(catchError(_ => of([])));
  }

  private updateRoleData(roleAssignments: AssignedRole[], roleLimits: VariantRoleAssignmentLimitResponse[]): void {
    this.groupRoleLimitsStrategyService.setTotalUsers(this.totalCount);

    this.cartridgeRoleAssignments = this.convertGroupRoleAssignments(roleAssignments, roleLimits);

    this.cartridgeRoleAssignmentDataService.setPreviousCartridgeRoleAssignmentsFromAssignedRoles(
      roleAssignments,
      this.groupId
    );
  }

  private aggregateGroupRoleAssignmentAlreadyProcessedCartridge(
    alreadyProcessedCartridge: CartridgeWithSelectableRoles,
    assignedRole: AssignedRole,
    selectableRoleToAdd: SelectableRole
  ): void {
    const alreadyProcessedRole = alreadyProcessedCartridge.roles.find(sr => sr.id === assignedRole.role.id);

    if (alreadyProcessedRole) {
      if (assignedRole.subOrganization) {
        const suborganizationData = {
          ...assignedRole.subOrganization,
          selected: true,
        };

        alreadyProcessedRole.suborganizationData = alreadyProcessedRole.suborganizationData
          ? [...alreadyProcessedRole.suborganizationData, suborganizationData]
          : [suborganizationData];
      }
    } else {
      alreadyProcessedCartridge.roles.push(selectableRoleToAdd);
    }
  }

  private convertGroupRoleAssignments(
    serviceRoleAssignments: AssignedRole[],
    roleLimits: VariantRoleAssignmentLimitResponse[]
  ): CartridgeWithSelectableRoles[] {
    return serviceRoleAssignments
      .reduce((acc: CartridgeWithSelectableRoles[], assignedRole: AssignedRole) => {
        const selectableRoleToAdd = {
          ...assignedRole.role,
          selected: true,
          suborganizationData: assignedRole.subOrganization
            ? [{ ...assignedRole.subOrganization, selected: true }]
            : [],
        };

        const alreadyProcessedCartridge = acc.find(
          cartridgeRoleAssignment => cartridgeRoleAssignment.id === assignedRole.role.cartridgeId
        );

        if (alreadyProcessedCartridge) {
          this.aggregateGroupRoleAssignmentAlreadyProcessedCartridge(
            alreadyProcessedCartridge,
            assignedRole,
            selectableRoleToAdd
          );

          return acc;
        }

        return [
          ...acc,
          {
            id: assignedRole.role.cartridgeId,
            name: assignedRole.role.localizedServiceName,
            roles: [selectableRoleToAdd],
          } as CartridgeWithSelectableRoles,
        ];
      }, [])
      .map(cartridgeWithSelectableRole => ({
        ...cartridgeWithSelectableRole,
        limits: this.groupRoleLimitsStrategyService.buildLimit(cartridgeWithSelectableRole, roleLimits),
      }));
  }
}
